import { useEffect, useState } from "react";

import {
  Box,
  Card,
  CardBody,
  FormControl,
  FormHelperText,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast
} from "@chakra-ui/react";

import { fetchGroupsForUser } from "~/api/groups";
import { createGroup } from "~/repositories/groupRepository";
import { useAppStore } from "~/store";

interface GroupUsageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  // Optional props for secret functionality
  groupNameOverride?: string;
  descriptionCallback?: (usage: string) => string;
  // Option to suppress the success toast
  suppressToast?: boolean;
}

export const GroupUsageDialog = ({
  isOpen,
  onClose,
  userId,
  groupNameOverride,
  descriptionCallback,
  suppressToast = false
}: GroupUsageDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUsage, setSelectedUsage] = useState<string | null>(null);
  const [customGroupName, setCustomGroupName] = useState("");
  const [customGroupNameWarning, setCustomGroupNameWarning] = useState("");
  const [customGroupDescriptionWarning, setCustomGroupDescriptionWarning] = useState("");
  const toast = useToast();
  const setUserHomeData = useAppStore((state) => state.setUserHomeData);
  const userHomeData = useAppStore((state) => state.userHomeData);

  // Add keyboard event handler for 'g' key and Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      // Skip if the key is pressed while any input element is focused
      if (["INPUT", "TEXTAREA", "SELECT"].includes((document.activeElement?.tagName || "").toUpperCase())) {
        return;
      }

      // Close on 'g' keypress
      if (event.key === "g" || event.key === "G") {
        onClose();
      }

      // Allow Escape to close the dialog
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Clean up event listener
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleUsageSelection = async (usage: string) => {
    // If "other" is selected, just set it as the selected type but don't create yet
    if (usage === "other" && !customGroupName.trim()) {
      setSelectedUsage(usage);
      return;
    }

    setIsLoading(true);
    try {
      // Create a default group based on the selected option
      let groupName = "";
      let groupDescription = "";

      // If groupNameOverride is provided, use it instead of the default names
      if (groupNameOverride) {
        groupName = groupNameOverride;
        // Use the callback if provided, or fall back to default description
        groupDescription = descriptionCallback ? descriptionCallback(usage) : `Group for ${usage}`;
      } else {
        // Default behavior without overrides
        switch (usage) {
          case "myself":
            groupName = "Solo";
            groupDescription = "Managing my health";
            break;
          case "family":
            groupName = "My Family";
            groupDescription = "Managing my family's health";
            break;
          case "clinic":
            groupName = "My Clinic";
            groupDescription = "Managing my clinic";
            break;
          case "other":
            groupName = customGroupName.trim();
            groupDescription = `Custom purpose: ${customGroupName.trim()}`; // More generic description
            break;
          default:
            groupName = "My Group";
            groupDescription = `Managing ${usage}`;
        }
      }

      // Create the group with proper fields based on the Group type
      const response = await createGroup({
        name: groupName,
        description: groupDescription,
        status: "active",
        // Add specific API parameters in the fetch body
        profileImageUrl: "",
        // Additional parameters that will be sent in the request body but not in the type
        ...{ addSelfAsMember: true, is_public: false }
      });

      // Only show the toast if suppressToast is false
      if (!suppressToast) {
        toast({
          title: usage === "other" ? "Setup complete" : "Group created",
          description:
            usage === "other"
              ? "Your preferences have been saved successfully."
              : `Your '${groupName}' group has been created successfully.`,
          status: "success",
          duration: 5000,
          isClosable: true
        });
      }

      // Fetch all groups and update the current group in the store
      try {
        // Fetch all groups and find the one we just created
        const groupsResponse = await fetchGroupsForUser();
        if (groupsResponse.success) {
          // Find the group we just created (should be the one with matching name)
          const createdGroup = [...(groupsResponse.ownedGroups || []), ...(groupsResponse.memberOfGroups || [])].find(
            (group) => group.name === groupName
          );

          if (createdGroup && userHomeData) {
            // Update userHomeData with the new group as default
            setUserHomeData({
              ...userHomeData,
              defaultGroup: createdGroup
            });
          }
        }
      } catch (error) {
        console.error("Error setting current group:", error);
      }

      onClose();

      // Open the InitialMemberDialogue after closing the group dialog
      setTimeout(() => {
        // Dispatch a custom event to open the InitialMemberDialogue
        window.dispatchEvent(new CustomEvent("open-initial-member-dialogue"));
      }, 300); // Small delay to ensure smooth transition
    } catch (error) {
      console.error("Error creating group:", error);
      // Always show error toasts, even if success toasts are suppressed
      toast({
        title: "Error creating group",
        description: "There was an error creating your group. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomGroupSubmit = async () => {
    if (!customGroupName.trim()) {
      setCustomGroupNameWarning("Input required");
      setCustomGroupDescriptionWarning("Please describe your goals before submitting");
      return;
    }

    // Now let the regular usage selection function handle the group creation
    await handleUsageSelection("other");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false} closeOnEsc={true}>
      <ModalOverlay />
      <ModalContent maxW="500px">
        <ModalHeader>Welcome</ModalHeader>
        <ModalBody>
          <VStack spacing={6} align="stretch">
            <Text>What will you use Henophess for?</Text>

            <VStack spacing={4}>
              <Card
                as="button"
                onClick={() => handleUsageSelection("myself")}
                variant="outline"
                width="100%"
                _hover={{ boxShadow: "md", borderColor: "brand.primary" }}
                cursor="pointer"
                opacity={isLoading ? 0.7 : 1}
                pointerEvents={isLoading ? "none" : "auto"}
                bg={selectedUsage === "myself" ? "brand.primary_light" : undefined}
                borderColor={selectedUsage === "myself" ? "brand.primary" : undefined}
              >
                <CardBody>
                  <Heading size="md">Myself</Heading>
                  <Text pt={2}>I'll use Henophess for my personal health tracking</Text>
                </CardBody>
              </Card>

              <Card
                as="button"
                onClick={() => handleUsageSelection("family")}
                variant="outline"
                width="100%"
                _hover={{ boxShadow: "md", borderColor: "brand.primary" }}
                cursor="pointer"
                opacity={isLoading ? 0.7 : 1}
                pointerEvents={isLoading ? "none" : "auto"}
                bg={selectedUsage === "family" ? "brand.primary_light" : undefined}
                borderColor={selectedUsage === "family" ? "brand.primary" : undefined}
              >
                <CardBody>
                  <Heading size="md">Supporting my family's health</Heading>
                  <Text pt={2}>I'll use Henophess to manage health information for my family</Text>
                </CardBody>
              </Card>

              <Card
                as="button"
                onClick={() => handleUsageSelection("clinic")}
                variant="outline"
                width="100%"
                _hover={{ boxShadow: "md", borderColor: "brand.primary" }}
                cursor="pointer"
                opacity={isLoading ? 0.7 : 1}
                pointerEvents={isLoading ? "none" : "auto"}
                bg={selectedUsage === "clinic" ? "brand.primary_light" : undefined}
                borderColor={selectedUsage === "clinic" ? "brand.primary" : undefined}
              >
                <CardBody>
                  <Heading size="md">Managing a clinic</Heading>
                  <Text pt={2}>I'll use Henophess to manage health information for my clinic patients</Text>
                </CardBody>
              </Card>

              <Card
                as="button"
                onClick={() => setSelectedUsage("other")}
                variant="outline"
                width="100%"
                _hover={{ boxShadow: "md", borderColor: "brand.primary" }}
                cursor="pointer"
                opacity={isLoading ? 0.7 : 1}
                pointerEvents={isLoading ? "none" : "auto"}
                bg={selectedUsage === "other" ? "brand.primary_light" : undefined}
                borderColor={selectedUsage === "other" ? "brand.primary" : undefined}
              >
                <CardBody>
                  <Heading size="md">Other</Heading>
                  <Text pt={2}>I'll use Henophess for a different purpose</Text>
                </CardBody>
              </Card>

              {selectedUsage === "other" && (
                <FormControl mt={2}>
                  <Input
                    placeholder="Describe your goals"
                    value={customGroupName}
                    onChange={(e) => setCustomGroupName(e.target.value)}
                    isDisabled={isLoading}
                    autoFocus
                  />
                  <FormHelperText>Tell us how you plan to use Henophess</FormHelperText>
                  <Box textAlign="right" mt={2}>
                    <Card
                      as="button"
                      onClick={handleCustomGroupSubmit}
                      variant="solid"
                      width="auto"
                      display="inline-block"
                      px={4}
                      py={2}
                      bg="brand.primary"
                      color="black"
                      _hover={{ bg: "brand.primary_dark" }}
                      cursor="pointer"
                      opacity={isLoading || !customGroupName.trim() ? 0.7 : 1}
                      pointerEvents={isLoading || !customGroupName.trim() ? "none" : "auto"}
                    >
                      <Text fontWeight="bold">Submit</Text>
                    </Card>
                  </Box>
                </FormControl>
              )}
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Box w="100%" textAlign="center">
            {/* Footer text removed as requested */}
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GroupUsageDialog;
